// PIN encryption using Web Crypto API.
// Algorithm matches E6's Java reference: RSA/ECB/OAEPwithSHA-256andMGF1Padding + AES/CBC/PKCS5PADDING.
// See cmd/e6-cli/embed/encrypt_pin.html for the original standalone implementation.

// The E6 public key comes as a base64 string
// but crypto.subtle.importKey requires raw bytes (an ArrayBuffer or Uint8Array)
function base64ToBytes(b64) {
    return Uint8Array.from(atob(b64), function (c) {
        return c.charCodeAt(0);
    });
}

// The Web Crypto API returns encrypted data as ArrayBuffer
// We need base64 strings to send them to the server in form fields.
function bytesToBase64(buf) {
    return btoa(String.fromCharCode.apply(null, new Uint8Array(buf)));
}

// Strips PEM headers if present, normalizes whitespace, and decodes the base64 key to raw bytes.
function parsePublicKeyBytes(publicKeyRaw) {
    var normalizedKey = publicKeyRaw;

    if (normalizedKey.indexOf('-----') !== -1) {
        normalizedKey = normalizedKey.replace(/-----[^-]+-----/g, '');
    }

    normalizedKey = normalizedKey.trim().replace(/\s/g, '');

    return base64ToBytes(normalizedKey);
}

async function encryptPIN(pin, publicKeyRaw) {
    var keyBytes = parsePublicKeyBytes(publicKeyRaw);

    var rsaKey = await crypto.subtle.importKey(
        'spki',
        keyBytes,
        { name: 'RSA-OAEP', hash: 'SHA-256' },
        false,
        ['wrapKey']
    );

    var aesKey = await crypto.subtle.generateKey(
        { name: 'AES-CBC', length: 256 },
        true,
        ['encrypt']
    );

    // Zero IV per E6 spec (new byte[16] in reference code).
    var iv = new Uint8Array(16);
    var pinBytes = new TextEncoder().encode(pin);
    var ciphertext = await crypto.subtle.encrypt(
        { name: 'AES-CBC', iv: iv },
        aesKey,
        pinBytes
    );

    var wrappedKey = await crypto.subtle.wrapKey('raw', aesKey, rsaKey, {
        name: 'RSA-OAEP'
    });

    // Checksum: HMAC-SHA-256(key=aesKeyBytes, data=plainPinBytes), hex-encoded.
    var aesKeyBytes = await crypto.subtle.exportKey('raw', aesKey);
    var hmacKey = await crypto.subtle.importKey(
        'raw',
        aesKeyBytes,
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
    );
    var hmacBytes = await crypto.subtle.sign('HMAC', hmacKey, pinBytes);
    var checksum = Array.from(new Uint8Array(hmacBytes))
        .map(function (b) {
            return b.toString(16).padStart(2, '0');
        })
        .join('');

    return {
        encryptedSymmetricKey: bytesToBase64(wrappedKey),
        encryptedPin: bytesToBase64(ciphertext),
        checksum: checksum
    };
}

window.encryptPIN = encryptPIN;
